import React, {useEffect, useMemo, useState} from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrTooltipWrapper from '../../../components/common/arr-tooltip-wrapper/ArrTooltipWrapper';

import ArrModal from '../../common/arr-modal/ArrModal';
import {t} from 'i18next';
import ArrButton from '../../common/arr-button/ArrButton';
import './BulkUserListView.scss';

import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import ArrToast from '../../common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../assets/images/close-toast.svg';

import {BulkActionToolTip, RightsTooltip} from './BulkActionTooltip';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  useBulkSecurityGroups,
  useBulkUserAccess,
  useBulkUserRights,
  useSaveBulkAccess,
  useUserMeta,
} from '../../../hooks/users/useUsers';
import {ISelectItem, Role} from '../../../shared/types';
import {
  emptyUserRights,
  IGetBulkUserRightsPayload,
  IUserRights,
  PERMISSIONS_CONFIG,
} from '../../../api/types/user.type';
import {ReactComponent as CheckboxDisabledIcon} from '../../../assets/images/disabled-checkbox.svg';
import {ReactComponent as CheckboxUncheckedIcon} from '../../../assets/images/active-unchecked.svg';
import {ReactComponent as CheckboxCheckedIcon} from '../../../assets/images/active-checked.svg';
import {ReactComponent as RadioActiveIcon} from '../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioInactiveIcon} from '../../../assets/images/radio-inactive-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../assets/images/radio-enable-icon.svg';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import SuccessIcon from '@mui/icons-material/Done';
import classNames from 'classnames';
import {PermissionSection} from '../../../shared/constants';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useDispatch, useSelector} from 'react-redux';
import {
  setHandleDrawerOpen,
  setOpenUnsavedModal,
  setUnSavedChanges,
} from '../../../redux/slice/bulkUsersSlice';
import {RootState} from '../../../redux/store';

type BulkUserListViewProps = {
  onUsersBulkUpdated?: () => void;
};

const BulkUserListView: React.FC<BulkUserListViewProps> = ({
  onUsersBulkUpdated,
}) => {
  const dispatch = useDispatch();
  const {data, isLoading: isLoadingBulkAccessView} = useBulkUserAccess(true);

  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedProject, setSelectedProject] = useState<ISelectItem | null>(
    null
  );
  const unSavedModal = useSelector(
    (state: RootState) => state.bulkUser.openUnsavedModal
  );
  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(null);
  const [editableRights, setEditableRights] =
    useState<IUserRights>(emptyUserRights);

  const [showToast, setShowToast] = useState<boolean>(false);
  const [action, setAction] = useState('');
  const [disableSave, setDisableSave] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedAssignedList, setSelectedAssignedList] = useState<KeyValue[]>(
    []
  );
  const hasUnsavedChanges = selectedAssignedList.length > 0 || selectedRole;
  useEffect(() => {
    if (selectedAssignedList.length === 0) {
      setAction('');
      setDisableSave(true);
    }
  }, [selectedAssignedList]);

  const {data: userMeta} = useUserMeta();

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const roleOptions = useMemo(() => {
    if (!data) return [];
    return data.bulkAccessRoles.map((role) => ({
      id: role.value,
      name: role.text,
      encryptedID: role.encryptedID,
      selected: role.selected,
    }));
  }, [data]);

  const projectOptions: ISelectItem[] = useMemo(() => {
    if (loginUserData.roleId == 1) {
      return (
        userMeta?.projectListSelect
          ?.filter((item) => item.key == userMeta.projectId)
          .map((project) => ({
            id: project.key,
            name: project.value,
            isSelected: project.isSelected,
          })) ?? []
      );
    }
    if (!data || !Array.isArray(data.bulkAccessProjects)) return [];

    return data.bulkAccessProjects.map((project) => ({
      id: project.key,
      name: project.value,
      isSelected: project.isSelected,
    }));
  }, [data, loginUserData, userMeta]);

  useEffect(() => {
    if (!selectedProject && projectOptions.length > 0) {
      const defaultProject = projectOptions.find((p) => p.isSelected) || null;
      setSelectedProject(defaultProject);
    }
  }, [projectOptions, selectedProject]);

  const projectId = selectedProject?.id;

  const isProjectAdmin = (role: Role | null) =>
    role?.id === 1 || role?.name === 'Project Administrator';

  const {data: securityGroupData} = useBulkSecurityGroups(
    projectId as number,
    !!projectId && !isProjectAdmin(selectedRole)
  );

  const securityGroupOptions: ISelectItem[] = useMemo(() => {
    return (securityGroupData ?? []).map((group: any) => ({
      id: Number(group.key),
      name: group.value ?? '',
    }));
  }, [securityGroupData]);

  useEffect(() => {
    setSelectedAssignedList([]);
    setSelectedProject(null);
    setSelectedRole(null);
    dispatch(setUnSavedChanges(false));
  }, []);

  const {data: userRightsData, isLoading: isUserRightsLoading} =
    useBulkUserRights(
      selectedRole?.encryptedID as unknown as number,
      selectedProject?.id !== undefined
        ? Number(selectedProject.id)
        : undefined,

      selectedSecurityGroup?.id !== undefined
        ? Number(selectedSecurityGroup.id)
        : undefined,

      Boolean(selectedRole?.encryptedID)
    );

  const {mutate: saveBulkAccessMutation, isPending: isSaveBulkAccessLoading} =
    useSaveBulkAccess();

  const handleRoleChange = (newValue: ISelectItem | null) => {
    dispatch(setUnSavedChanges(true));
    if (newValue) {
      setSelectedRole(newValue as unknown as Role);
      if (loginUserData.roleId != 1) {
        setSelectedProject(null);
      } else {
        setSelectedProject(projectOptions[0]);
      }
      setSelectedSecurityGroup(null);
      setSelectedAssignedList([]);
    } else {
      setSelectedRole(null);
    }
  };

  const handleProjectChange = (newValue: ISelectItem | null) => {
    if (newValue) {
      setSelectedProject(newValue);
      setSelectedSecurityGroup(null);
      setSelectedAssignedList([]);
    } else {
      setSelectedProject(null);
    }
  };

  const isSaveDisabled =
    !action || selectedAssignedList.length === 0 || disableSave;

  const handleSave = () => {
    if (!selectedRole || !selectedProject) return;

    const payload: IGetBulkUserRightsPayload = {
      userId: 0,
      roleId: selectedRole.id,
      projectId: Number(selectedProject.id),
      securityGroupId: Number(selectedSecurityGroup?.id ?? 0),
      bulkRoleID: selectedRole.id,
      ...Object.fromEntries(
        Object.entries(editableRights).map(([key, value]) => [
          key,
          value ?? false,
        ])
      ),
      accessRight: action === 'enable',
      selectedUserID: selectedAssignedList.map((u) => u.id),
    };

    if (showConfirm) {
      setShowConfirm(false);
    }

    saveBulkAccessMutation(payload, {
      onSuccess: () => {
        setShowToast(true);
        setSelectedAssignedList([]);
        dispatch(setUnSavedChanges(false));

        // Trigger grid refresh if callback provided
        if (onUsersBulkUpdated) {
          onUsersBulkUpdated();
        }
      },
      onError: (error) => {
        console.error('Error saving bulk access', error);
      },
    });
  };

  const modalNoDontSave = () => {
    setShowConfirm(false);
    dispatch(setOpenUnsavedModal(false));
    dispatch(setHandleDrawerOpen(false));
  };

  const modalSaveChanges = () => {
    setShowConfirm(false);
    dispatch(setOpenUnsavedModal(false));
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAction = event.target.value;
    setAction(selectedAction);

    if (selectedAction === 'disable' || selectedAction === 'enable') {
      setDisableSave(false);
    } else {
      setDisableSave(true);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      dispatch(setHandleDrawerOpen(false));
    }
  };

  const isRadioEnabled = selectedAssignedList.length > 0;

  const radioOptions = [
    {
      value: 'disable',
      label: 'Disable selection(s)',
    },
    {
      value: 'enable',
      label: 'Enable selection(s)',
    },
  ];

  useEffect(() => {
    if (selectedRole && userRightsData?.userRights) {
      setEditableRights(userRightsData.userRights);
    } else {
      setEditableRights(emptyUserRights);
    }
  }, [selectedRole, userRightsData]);

  const handleRightChange = (key: string) => {
    setEditableRights((prev) => {
      const updated = {...prev};
      const isChecked = !!prev[key as keyof typeof updated];

      updated[key as keyof typeof updated] = !isChecked;

      return updated;
    });
  };

  const isProjectAdminCheck =
    selectedRole?.name?.toLowerCase().trim() === 'project administrator';

  const isSecurityGroupSelected = Boolean(selectedSecurityGroup?.id);

  const checkboxesDisabled = !selectedProject?.id
    ? true
    : isProjectAdminCheck
      ? false
      : !isSecurityGroupSelected;

  const usersList =
    userRightsData?.usersList?.map((user) => ({
      id: user.userID,
      label: `${user.userName}`,
    })) ?? [];

  const handleAssignUserChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    setSelectedAssignedList((prev) =>
      event.target.checked
        ? [...prev, field]
        : prev.filter((item) => item.id !== field.id)
    );
  };

  return (
    <Box className="loading-overlay">
      {isLoadingBulkAccessView ||
      isSaveBulkAccessLoading ||
      isUserRightsLoading ? (
        <ArrLoadingSpinner
          show={
            isLoadingBulkAccessView ||
            isSaveBulkAccessLoading ||
            isUserRightsLoading
          }
        />
      ) : (
        <Box>
          <div
            className="bulk-access-action"
            data-testid={`bulk-user-list-view-bulk-user-access-control-cancel-save`}
          >
            <div
              className="bulk-access-label"
              data-testid={`bulk-user-list-view-bulk-user-access-control`}
            >
              <Typography
                className="typo-text headline3_size bulk-access-title"
                data-testid={`bulk-user-list-view-bulk-user-access-control-text`}
              >
                {t('userManagement.bulkAction.bulkUserAccessControl')}
              </Typography>
              <ArrTooltipWrapper
                title={
                  <Typography
                    className="typo-text headline3_size"
                    data-testid={`bulk-user-list-view-bulk-user-access-control-btn`}
                  >
                    {t('userManagement.bulkAction.bulkUserAccessControl')}
                  </Typography>
                }
                trigger={<InfoIcon fontSize="medium" className="info-icon" />}
                children={<BulkActionToolTip />}
              />
            </div>

            <ArrButton
              id="bulk-access-cancel-button"
              name="bulk-access-cancel-button"
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              dataTestId="bulk-user-list-view-cancel-button"
              className="cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              id="bulk-access-save-button"
              name="bulk-access-save-button"
              variant="primary"
              size="medium"
              onClick={handleSave}
              dataTestId="bulk-user-save-button"
              className="save-button"
              disabled={isSaveDisabled}
            >
              {t('common.save')}
            </ArrButton>
          </div>
          <Box className="bulk-access-control">
            <Box className="select-dropdown-container">
              <ArrSingleSelectDropdown
                dropdownLabel={t('userManagement.bulkAction.selectARole')}
                dataTestId={t('userManagement.bulkAction.selectARole')}
                dropdownItems={roleOptions}
                dropdownItemKey="id"
                dropdownItemDisplay="name"
                selectedValues={selectedRole}
                onSelectionChange={handleRoleChange}
                disabled={roleOptions.length === 0}
              />

              <ArrSingleSelectDropdown
                dropdownLabel={t('userManagement.bulkAction.selectAProject')}
                dataTestId={t('userManagement.bulkAction.selectAProject')}
                dropdownItems={projectOptions}
                dropdownItemKey="id"
                dropdownItemDisplay="name"
                selectedValues={selectedProject}
                onSelectionChange={handleProjectChange}
                disabled={
                  !selectedRole ||
                  projectOptions.length === 0 ||
                  loginUserData.roleId == 1
                }
                searchable
                showNoResultsText
              />

              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'userManagement.bulkAction.selectASecurityGroup'
                )}
                dataTestId={t('userManagement.bulkAction.selectASecurityGroup')}
                dropdownItems={securityGroupOptions}
                dropdownItemKey="id"
                dropdownItemDisplay="name"
                selectedValues={selectedSecurityGroup}
                onSelectionChange={setSelectedSecurityGroup}
                disabled={
                  !selectedProject ||
                  !selectedRole ||
                  selectedRole.id === 1 ||
                  selectedRole.name === 'Project Administrator'
                }
                searchable
                showNoResultsText
              />
            </Box>

            <Box className="actions-row">
              <RadioGroup row value={action} onChange={handleActionChange}>
                {radioOptions.map(({value, label}, index) => {
                  const isSelected = action === value;

                  return (
                    <FormControlLabel
                      data-testid={`action-row-form-control-label-${value}`}
                      key={value}
                      value={value}
                      control={
                        <Radio
                          slotProps={{
                            input: {
                              'aria-label': String(value),
                              'data-testid': `radio-${value}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>,
                          }}
                          disabled={!isRadioEnabled}
                          icon={
                            selectedAssignedList.length > 0 ? (
                              <RadioEnableIcon
                                data-testid={`bulk-assign-user-radio-enable-icon-${value}`}
                              />
                            ) : (
                              <RadioInactiveIcon
                                data-testid={`bulk-assign-user-radio-inactive-icon-${value}`}
                              />
                            )
                          }
                          checkedIcon={
                            <RadioActiveIcon
                              data-testid={`bulk-assign-user-radio-active-icon-${value}`}
                            />
                          }
                        />
                      }
                      label={
                        <span
                          className={classNames('bulk-radio-label', {
                            disabled: !isRadioEnabled,
                            active: isSelected && isRadioEnabled,
                            inactive: !isSelected && isRadioEnabled,
                          })}
                          data-testid={`bulk-user-list-view-label-${index}`}
                        >
                          {label}
                        </span>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Box>

            <Box className="main-content">
              <Box className="permission-group" data-testid="permission-group">
                {[
                  PermissionSection.Rights,
                  PermissionSection.Reports,
                  PermissionSection.DocViewControls,
                ].map((section) => (
                  <div
                    key={section}
                    className={classNames('rights-section', {
                      'reports-bottom-spacing':
                        section === PermissionSection.Reports,
                    })}
                    data-testid={`bulk-user-list-view-${section}-div`}
                  >
                    <div
                      key={section}
                      className="rights-section"
                      data-testid={`bulk-user-list-view-div-${section}-btn`}
                    >
                      <div
                        className="bulk-access-section-label"
                        data-testid={`bulk-user-list-view-div-${section}-button`}
                      >
                        <Typography
                          className="typo-text subhead4_size "
                          data-testid={`bulk-user-list-view-section-label-${section}`}
                        >
                          {section}
                        </Typography>

                        {section === PermissionSection.Rights && (
                          <ArrTooltipWrapper
                            placement="right"
                            trigger={
                              <InfoIcon
                                fontSize="medium"
                                className="info-icon-small"
                              />
                            }
                          >
                            <RightsTooltip />
                          </ArrTooltipWrapper>
                        )}
                      </div>

                      {PERMISSIONS_CONFIG.filter(
                        (item) => item.section === section
                      ).map((item, index) => {
                        if (item.label === 'Collections') return null;

                        const isDisabled = !selectedRole;
                        const isChecked = !!editableRights[item.key];

                        return (
                          <div
                            key={item.key}
                            className="rights-checkbox-container"
                            data-testid={`bulk-user-list-view-wrapper-${section}-${index}`}
                          >
                            <div
                              className={classNames('checkbox-label', {
                                disabled: isDisabled,
                              })}
                              onClick={() =>
                                !isDisabled && handleRightChange(item.key)
                              }
                              role="checkbox"
                              aria-checked={isChecked}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (
                                  (e.key === 'Enter' || e.key === ' ') &&
                                  !isDisabled
                                ) {
                                  handleRightChange(item.key);
                                }
                              }}
                              data-testid={`bulk-user-list-view-${section}-${item.key}`}
                            >
                              {isDisabled ? (
                                <CheckboxDisabledIcon className="checkbox-icon" />
                              ) : isChecked ? (
                                <CheckboxCheckedIcon className="checkbox-icon" />
                              ) : (
                                <CheckboxUncheckedIcon className="checkbox-icon" />
                              )}
                              <span
                                className={`${isDisabled ? 'disabled-label-text' : 'active-label-text'} typo-text body3_size`}
                                data-testid={`bulk-user-list-view-span-item-label-${section}-${index}`}
                              >
                                {item.label}
                              </span>
                              {section === PermissionSection.Reports && (
                                <div
                                  className="reports-bottom-spacing"
                                  data-testid={`bulk-user-list-view-div-link`}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </Box>

              {selectedProject?.id ? (
                <Box data-testid="assigned-user">
                  <ArrTransferList
                    showCheckbox={true}
                    selectedList={selectedAssignedList}
                    displayedList={usersList}
                    onChange={handleAssignUserChange}
                    checkboxDataTestId="bulk-user-list-view-assigned-users-transfer-list-checkbox"
                    title={t('userManagement.bulkAction.users')}
                    disable={checkboxesDisabled}
                  />
                </Box>
              ) : (
                <Box data-testid="assigned-user-disabled">
                  <ArrTransferList
                    showCheckbox={false}
                    selectedList={selectedAssignedList}
                    displayedList={[]}
                    onChange={handleAssignUserChange}
                    title={t('userManagement.bulkAction.users')}
                  />
                </Box>
              )}
            </Box>

            <ArrModal
              showModal={showConfirm || unSavedModal}
              showClose={false}
              onClose={() => setShowConfirm(false)}
              title={t('userManagement.assignUsers.unsavedTitle')}
              description={t('userManagement.assignUsers.unsavedDescription')}
              dataTestId="usermanagement-assignusers-modal"
              buttons={[
                {
                  id: '1',
                  label: t('userManagement.assignUsers.noDontSave'),

                  onClick: modalNoDontSave,

                  variant: 'secondary',
                  dataTestId: 'uma-dontsave',
                },
                {
                  id: '2',
                  label: t('userManagement.assignUsers.saveChanges'),
                  onClick: modalSaveChanges,
                  variant: 'primary',
                  dataTestId: 'uma-save',
                },
              ]}
            />

            {showToast && (
              <Box>
                <ArrToast
                  id="right-tick-toast"
                  type="success"
                  dismissable
                  onClose={() => setShowToast(false)}
                  showStartIcon
                  startIcon={<SuccessIcon className="right-tick-icon" />}
                  position="bottom-right"
                  className="right-tick-toast"
                />
                <ArrToast
                  id="success-toast"
                  title="Success"
                  message={t(
                    'userManagement.bulkAction.accessRightsAppliedSuccess'
                  )}
                  type="success"
                  dismissable
                  onClose={() => setShowToast(false)}
                  closeIcon={<CloseToast />}
                  position="bottom-right"
                  className="activation-toast"
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BulkUserListView;
